import { NavLink } from "react-router-dom";
import { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEdit,
  faBars,
  faDatabase,
  faTimes,
  faChevronDown,
  faChevronUp,
  faFileContract,
  faAngleDoubleLeft, // Icon for collapse
  faAngleDoubleRight // Icon for expand
} from "@fortawesome/free-solid-svg-icons";
import { FaUserAlt, FaQuestionCircle } from "react-icons/fa";

const StyleConstants = {
  sidebarGradient: 'linear-gradient(145deg, #ff9800, #f46f30, #e9491c)',
  sidebarTextColor: 'white',
  sidebarActiveBg: 'white',
  sidebarActiveText: '#e9491c',
  sidebarHoverBg: 'rgba(255, 255, 255, 0.1)',
  sidebarBorderRadius: '15px',
  surroundingPadding: '10px',
};


export default function Sidebar() {
  const userRole = localStorage.getItem("User_Role");
  const [isOpen, setIsOpen] = useState(window.innerWidth >= 768);
  const [isCandidate, setIsCandidate] = useState(false);
  const [isHR, setIsHR] = useState(false);
  const [isInterviewer, setIsInterviewer] = useState(false);
  const [hoveredLink, setHoveredLink] = useState(null);

  const toggleSidebar = () => setIsOpen(!isOpen);
  const toggleCandidate = () => setIsCandidate(!isCandidate);
  const toggleHR = () => setIsHR(!isHR);
  const toggleInterviewer = () => setIsInterviewer(!isInterviewer);

  useEffect(() => {
    const handleResize = () => {
        if (window.innerWidth < 768) {
            setIsOpen(false);
        }
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const sidebarContainerStyle = {
    height: '100vh', 
    display: 'flex',
    position: 'relative',
    padding: StyleConstants.surroundingPadding,
    boxSizing: 'border-box',
  };

  const sidebarStyle = (currentIsOpen) => ({
    backgroundImage: StyleConstants.sidebarGradient,
    color: StyleConstants.sidebarTextColor,
    transition: 'width 0.3s ease-in-out, min-width 0.3s ease-in-out',
    borderRadius: StyleConstants.sidebarBorderRadius,
    paddingTop: '0.5rem',
    paddingBottom: '0.5rem',
    display: 'flex',
    flexDirection: 'column',
    overflowX: 'hidden',
    width: currentIsOpen ? "16rem" : "4.5rem",
    minWidth: currentIsOpen ? "16rem" : "4.5rem",
    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
    position: 'relative',
    height: '100%',
  });

  const navLinkBaseStyle = {
    color: StyleConstants.sidebarTextColor,
    borderRadius: '8px',
    margin: '0.2rem 0.5rem',
    padding: '0.6rem 0.8rem',
    display: 'flex',
    alignItems: 'center',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    textDecoration: 'none',
    transition: 'background-color 0.2s ease, color 0.2s ease',
  };

  const getNavLinkStyle = (to, isSubmenu = false, isActive) => {
    let linkStyle = {
      ...navLinkBaseStyle,
      marginTop: isSubmenu ? '0.1rem' : '0.2rem',
      marginBottom: isSubmenu ? '0.1rem' : '0.2rem',
      fontSize: isSubmenu ? '0.9rem' : '1rem',
      color: isSubmenu ? 'rgba(255, 255, 255, 0.85)' : StyleConstants.sidebarTextColor,
    };
    if (isActive) {
      linkStyle = {
        ...linkStyle,
        backgroundColor: StyleConstants.sidebarActiveBg,
        color: StyleConstants.sidebarActiveText,
        fontWeight: isSubmenu ? 500 : 600,
      };
    } else if (hoveredLink === to) {
      linkStyle = { ...linkStyle, backgroundColor: StyleConstants.sidebarHoverBg };
    }
    return linkStyle;
  };
  
  const iconStyleObject = (isActiveParam) => ({
    marginRight: '0.75rem',
    width: '1.25em',
    textAlign: 'center',
    color: isActiveParam ? StyleConstants.sidebarActiveText : StyleConstants.sidebarTextColor,
    transition: 'color 0.2s ease',
  });

  const collapsibleHeaderStyle = (to) => ({
    ...navLinkBaseStyle,
    marginTop: '0.2rem',
    cursor: 'pointer',
    backgroundColor: hoveredLink === `toggle-${to}` ? StyleConstants.sidebarHoverBg : 'transparent',
  });

  const submenuUlStyle = { listStyleType: 'none', paddingLeft: '0.5rem' };
  const submenuLinkPaddingStyle = { paddingLeft: '1.5rem' };

  const mobileToggleButtonStyle = {
    position: 'fixed',
    top: `calc(${StyleConstants.surroundingPadding} + 10px)`, 
    left: `calc(${StyleConstants.surroundingPadding} + 15px)`,
    zIndex: 1051,
    backgroundColor: 'rgba(0,0,0,0.6)',
    color: 'white', border: 'none',
    padding: '0.4rem 0.65rem', borderRadius: '0.25rem',
    boxShadow: '0 2px 5px rgba(0,0,0,0.3)',
  };

  const persistentToggleButtonStyle = (currentIsOpen) => ({
    position: 'absolute',
    top: '10px',
    right: currentIsOpen ? '10px' : '50%',
    transform: currentIsOpen ? 'none' : 'translateX(50%)',
    zIndex: 10,
    background: 'none',
    color: StyleConstants.sidebarTextColor,
    border: 'none',
    padding: '0.3rem 0.5rem',
    borderRadius: '5px',
    cursor: 'pointer',
    transition: 'background-color 0.2s ease, right 0.35s ease-in-out, transform 0.35s ease-in-out',
  });
  const persistentToggleButtonHoverStyle = {
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
  };

  const renderNavLink = (to, icon, text, currentIsOpen, isSubmenu = false) => (
    <li className="nav-item">
      <NavLink
        to={to}
        onMouseEnter={() => setHoveredLink(to)}
        onMouseLeave={() => setHoveredLink(null)}
        className={({ isActive }) => isActive ? "active-sidebar-link" : ""}
        style={({ isActive }) => getNavLinkStyle(to, isSubmenu, isActive)}
      >
        {({ isActive }) => ( 
          <>
            {typeof icon === 'string' ? 
              <i className={`${icon} me-2`} style={iconStyleObject(isActive)}></i> : 
              <FontAwesomeIcon icon={icon} style={iconStyleObject(isActive)} className="me-2 fa-fw" />}
            {currentIsOpen ? text : ""}
          </>
        )}
      </NavLink>
    </li>
  );

  const renderCollapsibleSection = (label, iconComponent, isSectionOpen, toggleFunction, links, currentIsOpen) => (
    <li className="nav-item">
      <div
        onClick={toggleFunction}
        style={collapsibleHeaderStyle(label)}
        onMouseEnter={() => setHoveredLink(`toggle-${label}`)}
        onMouseLeave={() => setHoveredLink(null)}
        role="button"
        aria-expanded={isSectionOpen}
      >
        <span style={{ display: 'flex', alignItems: 'center', overflow: 'hidden' }}>
          <span style={iconStyleObject(false)}>{iconComponent}</span>
          {currentIsOpen ? label : ""}
        </span>
        {currentIsOpen && (
            <FontAwesomeIcon
            icon={isSectionOpen ? faChevronUp : faChevronDown}
            style={{ marginLeft: 'auto', color: StyleConstants.sidebarTextColor, transition: 'transform 0.2s ease-in-out' }}
            />
        )}
      </div>
      {currentIsOpen && isSectionOpen && (
        <ul style={submenuUlStyle}>
          {links.map(link => (
            <li className="nav-item" key={link.to}>
              <NavLink
                to={link.to}
                onMouseEnter={() => setHoveredLink(link.to)}
                onMouseLeave={() => setHoveredLink(null)}
                className={({ isActive }) => isActive ? "active-sidebar-link" : ""}
                style={({ isActive }) => ({
                    ...getNavLinkStyle(link.to, true, isActive),
                    ...submenuLinkPaddingStyle
                })}
              >
                {({ isActive }) => ( 
                  <>
                    {typeof link.icon === 'string' ? 
                      <i className={`${link.icon} me-2`} style={iconStyleObject(isActive)}></i> : 
                      <FontAwesomeIcon icon={link.icon} style={iconStyleObject(isActive)} className="me-2 fa-fw"/>}
                    {currentIsOpen ? link.text : ""}
                  </>
                )}
              </NavLink>
            </li>
          ))}
        </ul>
      )}
    </li>
  );

  // --- MODIFIED: Updated candidate links ---
  const candidateLinks = [
    { to: "/jobapplication", icon: "fas fa-user", text: "Create Profile" },
    { to: "/job-openings", icon: "fas fa-briefcase", text: "Job Openings" },
    { to: "/business-opportunities", icon: "fas fa-lightbulb", text: "Business Opportunity" },
    { to: "/consultancy-opportunities", icon: "fas fa-user-tie", text: "Technical Consultant" },
   
    { to: "/DocumentUpload", icon: "fas fa-file-alt", text: "Upload Document" },
    { to: "/contact-us", icon: "fas fa-headset", text: "Contact Us" }
  ];

  const hrLinks = [
    { to: "/Jobposting", icon: "fas fa-plus-square", text: " Posting" },
    { to: "/jobapplicationresult", icon: "fas fa-users", text: "Applicants" },
    { to: "/interviewSchedule", icon: "fas fa-calendar-alt", text: "Schedule Interview" },
    { to: "/InterviewerFeedback", icon: "fas fa-comments", text: "Interview Evaluation" },
    { to: "/DocumentViewList", icon: "fas fa-folder-open", text: "Document View" },
    { to: "/letterform", icon: faFileContract, text: "Offer Letter" },
    { to: "/appoinmentletterform", icon: faFileContract, text: "Appointment Letter" },
    { to: "/InterviewerDetailList", icon: "fas fa-id-badge", text: "Interviewers List" },
    { to: "/resume-bank", icon: faDatabase, text: "Resume Bank" }
  ];
  const interviewerLinks = [
    { to: "/InterviewerForm", icon: "fas fa-user-edit", text: "Interviewer Form" },
    { to: "/evaluationForm", icon: "fas fa-clipboard-check", text: "Evaluation Form" },
  ];

  return (
    <div style={sidebarContainerStyle}>
      <button 
        className="d-md-none"
        style={{
            ...mobileToggleButtonStyle,
            left: isOpen && window.innerWidth < 768 
                ? `calc(4.5rem + ${StyleConstants.surroundingPadding} + 15px)`
                : `calc(${StyleConstants.surroundingPadding} + 15px)`, 
            transition: 'left 0.3s ease-in-out',
        }} 
        onClick={toggleSidebar}
       >
        <FontAwesomeIcon icon={isOpen ? faTimes : faBars} />
      </button>

      <nav
        className={`shadow ${isOpen ? "open" : ""}`}
        style={sidebarStyle(isOpen)}
      >
        <button
          onClick={toggleSidebar}
          style={{
            ...persistentToggleButtonStyle(isOpen),
            ...(hoveredLink === 'persistent-toggle' ? persistentToggleButtonHoverStyle : {})
          }}
          onMouseEnter={() => setHoveredLink('persistent-toggle')}
          onMouseLeave={() => setHoveredLink(null)}
          aria-label={isOpen ? "Collapse sidebar" : "Expand sidebar"}
          className="d-none d-md-block"
        >
          <FontAwesomeIcon icon={isOpen ? faAngleDoubleLeft : faAngleDoubleRight} />
        </button>

        <ul className="nav nav-pills flex-column mb-auto text-start" style={{ marginTop: isOpen ? '40px' : '45px' }}>
          {userRole === "Admin" && (
            <>
              {renderNavLink("/Admindashboard", "fas fa-home", "Home", isOpen)}
              {renderNavLink("/crudHome", "fas fa-database", "Admin Panel", isOpen)}
              {renderNavLink("/create-questions", faEdit, "Create Questions", isOpen)}
              {renderNavLink("/edit-dashboard", faEdit, "Edit Dashboard", isOpen)}
              {renderCollapsibleSection("Candidate", <FaUserAlt />, isCandidate, toggleCandidate, candidateLinks, isOpen)}
              {renderCollapsibleSection("HR", <FaUserAlt />, isHR, toggleHR, hrLinks, isOpen)}
              {renderCollapsibleSection("Interviewer", <FaUserAlt />, isInterviewer, toggleInterviewer, interviewerLinks, isOpen)}
              {renderNavLink("/feedback-list", "fas fa-list-alt", "Feedback List", isOpen)}
            </>
          )}
           {userRole === "Candidate" && (
            <>
              {renderNavLink("/dashboard", "fas fa-home", "Home", isOpen)}
              {candidateLinks.map(link => renderNavLink(link.to, link.icon, link.text, isOpen))}
            </>
          )}
          {userRole === "HR" && (
            <>
              {renderNavLink("/HRDashboard", "fas fa-home", "Home", isOpen)}
              {hrLinks.map(link => renderNavLink(link.to, link.icon, link.text, isOpen))}
            </>
          )}
          {userRole === "Interviewer" && (
            <>
              {renderNavLink("/InterviewerDashboard", "fas fa-home", "Home", isOpen)}
              {interviewerLinks.map(link => renderNavLink(link.to, link.icon, link.text, isOpen))}
            </>
          )}
        </ul>
      </nav>
      <style jsx global>{`
        .active-sidebar-link {}
        .active-sidebar-link i,
        .active-sidebar-link svg {}
      `}</style>
    </div>
  );
}


// import { NavLink } from "react-router-dom";
// import { useState, useEffect } from "react";
// import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
// import {
//   faEdit,
//   faBars,
//   faDatabase,
//   faTimes,
//   faChevronDown,
//   faChevronUp,
//   faFileContract,
//   faAngleDoubleLeft, // Icon for collapse
//   faAngleDoubleRight // Icon for expand
// } from "@fortawesome/free-solid-svg-icons";
// import { FaUserAlt, FaQuestionCircle } from "react-icons/fa";

// const StyleConstants = {
//   sidebarGradient: 'linear-gradient(145deg, #ff9800, #f46f30, #e9491c)',
//   sidebarTextColor: 'white',
//   sidebarActiveBg: 'white',
//   sidebarActiveText: '#e9491c',
//   sidebarHoverBg: 'rgba(255, 255, 255, 0.1)',
//   sidebarBorderRadius: '15px',
//   // sidebarTopMargin: '20px', // We'll use padding on container now
//   surroundingPadding: '10px', // New constant for the padding around the sidebar
// };


// export default function Sidebar() {
//   const userRole = localStorage.getItem("User_Role");
//   const [isOpen, setIsOpen] = useState(window.innerWidth >= 768);
//   const [isCandidate, setIsCandidate] = useState(false);
//   const [isHR, setIsHR] = useState(false);
//   const [isInterviewer, setIsInterviewer] = useState(false);
//   const [hoveredLink, setHoveredLink] = useState(null);

//   const toggleSidebar = () => setIsOpen(!isOpen);
//   const toggleCandidate = () => setIsCandidate(!isCandidate);
//   const toggleHR = () => setIsHR(!isHR);
//   const toggleInterviewer = () => setIsInterviewer(!isInterviewer);

//   useEffect(() => {
//     const handleResize = () => {
//         if (window.innerWidth < 768) {
//             setIsOpen(false);
//         }
//     };
//     window.addEventListener("resize", handleResize);
//     return () => window.removeEventListener("resize", handleResize);
//   }, []);

//   const sidebarContainerStyle = {
//     height: '100vh', // Container takes full viewport height
//     display: 'flex',
//     position: 'relative',
//     padding: StyleConstants.surroundingPadding, // ADDED: Padding around the sidebar
//     boxSizing: 'border-box', // Ensures padding is included in height/width calculation
//   };

//   const sidebarStyle = (currentIsOpen) => ({
//     backgroundImage: StyleConstants.sidebarGradient,
//     color: StyleConstants.sidebarTextColor,
//     transition: 'width 0.3s ease-in-out, min-width 0.3s ease-in-out',
//     // Apply border radius to all corners since it's now fully contained
//     borderRadius: StyleConstants.sidebarBorderRadius,
//     paddingTop: '0.5rem',
//     paddingBottom: '0.5rem',
//     display: 'flex',
//     flexDirection: 'column',
//     overflowX: 'hidden',
//     width: currentIsOpen ? "16rem" : "4.5rem",
//     minWidth: currentIsOpen ? "16rem" : "4.5rem",
//     boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
//     position: 'relative',
//     height: '100%', // MODIFIED: Sidebar takes full height of its padded container
//     // marginTop: StyleConstants.sidebarTopMargin, // REMOVED: Container's padding handles this
//   });

//   const navLinkBaseStyle = {
//     color: StyleConstants.sidebarTextColor,
//     borderRadius: '8px',
//     margin: '0.2rem 0.5rem',
//     padding: '0.6rem 0.8rem',
//     display: 'flex',
//     alignItems: 'center',
//     whiteSpace: 'nowrap',
//     overflow: 'hidden',
//     textOverflow: 'ellipsis',
//     textDecoration: 'none',
//     transition: 'background-color 0.2s ease, color 0.2s ease',
//   };

//   const getNavLinkStyle = (to, isSubmenu = false, isActive) => {
//     let linkStyle = {
//       ...navLinkBaseStyle,
//       marginTop: isSubmenu ? '0.1rem' : '0.2rem',
//       marginBottom: isSubmenu ? '0.1rem' : '0.2rem',
//       fontSize: isSubmenu ? '0.9rem' : '1rem',
//       color: isSubmenu ? 'rgba(255, 255, 255, 0.85)' : StyleConstants.sidebarTextColor,
//     };
//     if (isActive) {
//       linkStyle = {
//         ...linkStyle,
//         backgroundColor: StyleConstants.sidebarActiveBg,
//         color: StyleConstants.sidebarActiveText,
//         fontWeight: isSubmenu ? 500 : 600,
//       };
//     } else if (hoveredLink === to) {
//       linkStyle = { ...linkStyle, backgroundColor: StyleConstants.sidebarHoverBg };
//     }
//     return linkStyle;
//   };
  
//   const iconStyleObject = (isActiveParam) => ({
//     marginRight: '0.75rem',
//     width: '1.25em',
//     textAlign: 'center',
//     color: isActiveParam ? StyleConstants.sidebarActiveText : StyleConstants.sidebarTextColor,
//     transition: 'color 0.2s ease',
//   });

//   const collapsibleHeaderStyle = (to) => ({
//     ...navLinkBaseStyle,
//     marginTop: '0.2rem',
//     cursor: 'pointer',
//     backgroundColor: hoveredLink === `toggle-${to}` ? StyleConstants.sidebarHoverBg : 'transparent',
//   });

//   const submenuUlStyle = { listStyleType: 'none', paddingLeft: '0.5rem' };
//   const submenuLinkPaddingStyle = { paddingLeft: '1.5rem' };

//   const mobileToggleButtonStyle = {
//     position: 'fixed',
//     // Adjust 'top' and 'left' to account for the container's padding
//     top: `calc(${StyleConstants.surroundingPadding} + 10px)`, 
//     left: `calc(${StyleConstants.surroundingPadding} + 15px)`,
//     zIndex: 1051,
//     backgroundColor: 'rgba(0,0,0,0.6)',
//     color: 'white', border: 'none',
//     padding: '0.4rem 0.65rem', borderRadius: '0.25rem',
//     boxShadow: '0 2px 5px rgba(0,0,0,0.3)',
//   };

//   const persistentToggleButtonStyle = (currentIsOpen) => ({
//     position: 'absolute',
//     top: '10px',
//     right: currentIsOpen ? '10px' : '50%',
//     transform: currentIsOpen ? 'none' : 'translateX(50%)',
//     zIndex: 10,
//     background: 'none',
//     color: StyleConstants.sidebarTextColor,
//     border: 'none',
//     padding: '0.3rem 0.5rem',
//     borderRadius: '5px',
//     cursor: 'pointer',
//     transition: 'background-color 0.2s ease, right 0.35s ease-in-out, transform 0.35s ease-in-out',
//   });
//   const persistentToggleButtonHoverStyle = {
//     backgroundColor: 'rgba(255, 255, 255, 0.2)',
//   };

//   const renderNavLink = (to, icon, text, currentIsOpen, isSubmenu = false) => (
//     <li className="nav-item">
//       <NavLink
//         to={to}
//         onMouseEnter={() => setHoveredLink(to)}
//         onMouseLeave={() => setHoveredLink(null)}
//         className={({ isActive }) => isActive ? "active-sidebar-link" : ""}
//         style={({ isActive }) => getNavLinkStyle(to, isSubmenu, isActive)}
//       >
//         {({ isActive }) => ( 
//           <>
//             {typeof icon === 'string' ? 
//               <i className={`${icon} me-2`} style={iconStyleObject(isActive)}></i> : 
//               <FontAwesomeIcon icon={icon} style={iconStyleObject(isActive)} className="me-2 fa-fw" />}
//             {currentIsOpen ? text : ""}
//           </>
//         )}
//       </NavLink>
//     </li>
//   );

//   const renderCollapsibleSection = (label, iconComponent, isSectionOpen, toggleFunction, links, currentIsOpen) => (
//     <li className="nav-item">
//       <div
//         onClick={toggleFunction}
//         style={collapsibleHeaderStyle(label)}
//         onMouseEnter={() => setHoveredLink(`toggle-${label}`)}
//         onMouseLeave={() => setHoveredLink(null)}
//         role="button"
//         aria-expanded={isSectionOpen}
//       >
//         <span style={{ display: 'flex', alignItems: 'center', overflow: 'hidden' }}>
//           <span style={iconStyleObject(false)}>{iconComponent}</span>
//           {currentIsOpen ? label : ""}
//         </span>
//         {currentIsOpen && (
//             <FontAwesomeIcon
//             icon={isSectionOpen ? faChevronUp : faChevronDown}
//             style={{ marginLeft: 'auto', color: StyleConstants.sidebarTextColor, transition: 'transform 0.2s ease-in-out' }}
//             />
//         )}
//       </div>
//       {currentIsOpen && isSectionOpen && (
//         <ul style={submenuUlStyle}>
//           {links.map(link => (
//             <li className="nav-item" key={link.to}>
//               <NavLink
//                 to={link.to}
//                 onMouseEnter={() => setHoveredLink(link.to)}
//                 onMouseLeave={() => setHoveredLink(null)}
//                 className={({ isActive }) => isActive ? "active-sidebar-link" : ""}
//                 style={({ isActive }) => ({
//                     ...getNavLinkStyle(link.to, true, isActive),
//                     ...submenuLinkPaddingStyle
//                 })}
//               >
//                 {({ isActive }) => ( 
//                   <>
//                     {typeof link.icon === 'string' ? 
//                       <i className={`${link.icon} me-2`} style={iconStyleObject(isActive)}></i> : 
//                       <FontAwesomeIcon icon={link.icon} style={iconStyleObject(isActive)} className="me-2 fa-fw"/>}
//                     {currentIsOpen ? link.text : ""}
//                   </>
//                 )}
//               </NavLink>
//             </li>
//           ))}
//         </ul>
//       )}
//     </li>
//   );

//   const candidateLinks = [
//     { to: "/jobapplication", icon: "fas fa-user", text: "Create Profile" },
//     // { to: "/personalityquizform", icon: "fas fa-brain", text: "Personality Quiz" },
//     { to: "/Jobpostingresult", icon: "fas fa-briefcase", text: "Job Openings" },
//     { to: "/DocumentUpload", icon: "fas fa-file-alt", text: "Upload Document" },
//     { to: "/contact-us", icon: "fas fa-headset", text: "Contact Us" }

     
//   ];
//   const hrLinks = [
//     { to: "/Jobposting", icon: "fas fa-plus-square", text: "Job Posting" },
//     { to: "/jobapplicationresult", icon: "fas fa-users", text: "Job Applicants" },
//     { to: "/interviewSchedule", icon: "fas fa-calendar-alt", text: "Schedule Interview" },
//     { to: "/InterviewerFeedback", icon: "fas fa-comments", text: "Interview Evaluation" },
//     { to: "/DocumentViewList", icon: "fas fa-folder-open", text: "Document View" },
//     { to: "/letterform", icon: faFileContract, text: "Offer Letter" },
//     { to: "/appoinmentletterform", icon: faFileContract, text: "Appointment Letter" },
//     { to: "/InterviewerDetailList", icon: "fas fa-id-badge", text: "Interviewers List" },
//     { to: "/resume-bank", icon: faDatabase, text: "Resume Bank" }
//   ];
//   const interviewerLinks = [
//     { to: "/InterviewerForm", icon: "fas fa-user-edit", text: "Interviewer Form" },
//     { to: "/evaluationForm", icon: "fas fa-clipboard-check", text: "Evaluation Form" },
//   ];

//   return (
//     // The div with sidebarContainerStyle is now the main element for the sidebar area
//     <div style={sidebarContainerStyle}>
//       {/* Mobile toggle button's position is now relative to the padded container */}
//       <button 
//         className="d-md-none"
//         style={{
//             ...mobileToggleButtonStyle,
//             left: isOpen && window.innerWidth < 768 
//                 ? `calc(4.5rem + ${StyleConstants.surroundingPadding} + 15px)` // Adjust if sidebar is open on mobile
//                 : `calc(${StyleConstants.surroundingPadding} + 15px)`, 
//             transition: 'left 0.3s ease-in-out',
//         }} 
//         onClick={toggleSidebar}
//        >
//         <FontAwesomeIcon icon={isOpen ? faTimes : faBars} />
//       </button>

//       {/* The nav element is the actual sidebar visuals */}
//       <nav
//         className={`shadow ${isOpen ? "open" : ""}`} // Removed h-100 here, will be 100% of parent
//         style={sidebarStyle(isOpen)}
//       >
//         <button
//           onClick={toggleSidebar}
//           style={{
//             ...persistentToggleButtonStyle(isOpen),
//             ...(hoveredLink === 'persistent-toggle' ? persistentToggleButtonHoverStyle : {})
//           }}
//           onMouseEnter={() => setHoveredLink('persistent-toggle')}
//           onMouseLeave={() => setHoveredLink(null)}
//           aria-label={isOpen ? "Collapse sidebar" : "Expand sidebar"}
//           className="d-none d-md-block"
//         >
//           <FontAwesomeIcon icon={isOpen ? faAngleDoubleLeft : faAngleDoubleRight} />
//         </button>

//                 <ul className="nav nav-pills flex-column mb-auto text-start" style={{ marginTop: isOpen ? '40px' : '45px' }}>
//           {userRole === "Admin" && (
//             <>
//               {renderNavLink("/Admindashboard", "fas fa-home", "Home", isOpen)}
//               {renderNavLink("/crudHome", "fas fa-database", "Admin Panel", isOpen)}
//               {renderNavLink("/create-questions", faEdit, "Create Questions", isOpen)}
//               {renderNavLink("/edit-dashboard", faEdit, "Edit Dashboard", isOpen)}
//               {renderCollapsibleSection("Candidate", <FaUserAlt />, isCandidate, toggleCandidate, candidateLinks, isOpen)}
//               {renderCollapsibleSection("HR", <FaUserAlt />, isHR, toggleHR, hrLinks, isOpen)}
//               {renderCollapsibleSection("Interviewer", <FaUserAlt />, isInterviewer, toggleInterviewer, interviewerLinks, isOpen)}
//               {renderNavLink("/feedback-list", "fas fa-list-alt", "Feedback List", isOpen)}
//               {/* {renderNavLink("/pdf-viewer", "fas fa-file-pdf", "Scan Pdf", isOpen)} */}
//               {/* {renderNavLink("/feedback", "fas fa-comment-dots", "Feedback", isOpen)} */}
//             </>
//           )}
//            {userRole === "Candidate" && (
//             <>
//               {renderNavLink("/dashboard", "fas fa-home", "Home", isOpen)}
//               {candidateLinks.map(link => renderNavLink(link.to, link.icon, link.text, isOpen))}
//               {/* {renderNavLink("/pdf-viewer", "fas fa-file-pdf", "Scan Pdf", isOpen)} */}
//               {/* {renderNavLink("/feedback", "fas fa-comment-dots", "Feedback", isOpen)} */}
//             </>
//           )}
//           {userRole === "HR" && (
//             <>
//               {renderNavLink("/HRDashboard", "fas fa-home", "Home", isOpen)}
//               {hrLinks.map(link => renderNavLink(link.to, link.icon, link.text, isOpen))}
//               {/* {renderNavLink("/pdf-viewer", "fas fa-file-pdf", "Scan Pdf", isOpen)} */}
//               {/* {renderNavLink("/feedback", "fas fa-comment-dots", "Feedback", isOpen)} */}
//               {/* {renderNavLink("/feedback-list", "fas fa-list-alt", "Feedback List", isOpen)} */}
//             </>
//           )}
//           {userRole === "Interviewer" && (
//             <>
//               {renderNavLink("/InterviewerDashboard", "fas fa-home", "Home", isOpen)}
//               {interviewerLinks.map(link => renderNavLink(link.to, link.icon, link.text, isOpen))}
//               {/* {renderNavLink("/feedback", "fas fa-comment-dots", "Feedback", isOpen)} */}
//             </>
//           )}
//         </ul>
//       </nav>
//       <style jsx global>{`
//         .active-sidebar-link {}
//         .active-sidebar-link i,
//         .active-sidebar-link svg {}
//       `}</style>
//     </div>
//   );
// }

// import { NavLink } from "react-router-dom";
// import { useState, useEffect } from "react";
// import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
// import {
//   faEdit,
//   faBars,
//   faDatabase,
//   faTimes,
//   faChevronDown,
//   faChevronUp,
//   faFileContract,
//   faAngleDoubleLeft, // Icon for collapse
//   faAngleDoubleRight // Icon for expand
// } from "@fortawesome/free-solid-svg-icons";
// import { FaUserAlt, FaQuestionCircle } from "react-icons/fa";

// // (StyleConstants remain the same)
// const StyleConstants = {
//   sidebarGradient: 'linear-gradient(145deg, #ff9800, #f46f30, #e9491c)',
//   sidebarTextColor: 'white',
//   sidebarActiveBg: 'white',
//   sidebarActiveText: '#e9491c',
//   sidebarHoverBg: 'rgba(255, 255, 255, 0.1)',
//   sidebarBorderRadius: '15px',
//   sidebarTopMargin: '20px',
// };


// export default function Sidebar() {
//   const userRole = localStorage.getItem("User_Role");
//   const [isOpen, setIsOpen] = useState(window.innerWidth >= 768); // Initialize based on screen width
//   const [isCandidate, setIsCandidate] = useState(false);
//   const [isHR, setIsHR] = useState(false);
//   const [isInterviewer, setIsInterviewer] = useState(false);
//   const [hoveredLink, setHoveredLink] = useState(null);

//   const toggleSidebar = () => setIsOpen(!isOpen);
//   const toggleCandidate = () => setIsCandidate(!isCandidate);
//   const toggleHR = () => setIsHR(!isHR);
//   const toggleInterviewer = () => setIsInterviewer(!isInterviewer);

//   useEffect(() => {
//     const handleResize = () => {
//         // On larger screens, if user manually collapsed, keep it collapsed.
//         // If user manually expanded, keep it expanded.
//         // If no manual interaction, default to open on large, closed on small.
//         if (window.innerWidth < 768) {
//             setIsOpen(false); // Always collapse on small screens unless manually opened after
//         } else {
//             // For larger screens, you might want to remember the last state
//             // or default to open. For simplicity, let's re-evaluate.
//             // If you want to persist user's choice, you'd need localStorage here.
//             // setIsOpen(true); // Or maintain current isOpen state
//         }
//     };
//     // Set initial state correctly without causing a fight with user interaction
//     // setIsOpen(window.innerWidth >= 768); // This was the previous logic

//     window.addEventListener("resize", handleResize);
//     return () => window.removeEventListener("resize", handleResize);
//   }, []); // Removed isOpen from dependency array to avoid re-triggering on manual toggle

//   // --- Styles (sidebarContainerStyle, sidebarStyle, navLinkBaseStyle, getNavLinkStyle, iconStyleObject, collapsibleHeaderStyle, submenuUlStyle, submenuLinkPaddingStyle, mobileToggleButtonStyle remain the same) ---
//   const sidebarContainerStyle = {
//     height: `calc(100vh - ${StyleConstants.sidebarTopMargin})`,
//     marginTop: StyleConstants.sidebarTopMargin,
//     display: 'flex',
//     position: 'relative',
//   };

//   const sidebarStyle = (currentIsOpen) => ({
//     backgroundImage: StyleConstants.sidebarGradient,
//     color: StyleConstants.sidebarTextColor,
//     transition: 'width 0.3s ease-in-out, min-width 0.3s ease-in-out', // Smoother transition
//     borderTopRightRadius: StyleConstants.sidebarBorderRadius,
//     borderBottomRightRadius: StyleConstants.sidebarBorderRadius,
//     paddingTop: '0.5rem',
//     paddingBottom: '0.5rem',
//     display: 'flex',
//     flexDirection: 'column',
//     overflowX: 'hidden',
//     width: currentIsOpen ? "16rem" : "4.5rem",
//     minWidth: currentIsOpen ? "16rem" : "4.5rem",
//     boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
//     position: 'relative', // Needed for absolute positioning of the persistent toggle button
//   });

//   const navLinkBaseStyle = {
//     color: StyleConstants.sidebarTextColor,
//     borderRadius: '8px',
//     margin: '0.2rem 0.5rem',
//     padding: '0.6rem 0.8rem',
//     display: 'flex',
//     alignItems: 'center',
//     whiteSpace: 'nowrap',
//     overflow: 'hidden',
//     textOverflow: 'ellipsis',
//     textDecoration: 'none',
//     transition: 'background-color 0.2s ease, color 0.2s ease',
//   };

//   const getNavLinkStyle = (to, isSubmenu = false, isActive) => {
//     let linkStyle = {
//       ...navLinkBaseStyle,
//       marginTop: isSubmenu ? '0.1rem' : '0.2rem',
//       marginBottom: isSubmenu ? '0.1rem' : '0.2rem',
//       fontSize: isSubmenu ? '0.9rem' : '1rem',
//       color: isSubmenu ? 'rgba(255, 255, 255, 0.85)' : StyleConstants.sidebarTextColor,
//     };
//     if (isActive) {
//       linkStyle = {
//         ...linkStyle,
//         backgroundColor: StyleConstants.sidebarActiveBg,
//         color: StyleConstants.sidebarActiveText,
//         fontWeight: isSubmenu ? 500 : 600,
//       };
//     } else if (hoveredLink === to) {
//       linkStyle = { ...linkStyle, backgroundColor: StyleConstants.sidebarHoverBg };
//     }
//     return linkStyle;
//   };
  
//   const iconStyleObject = (isActiveParam) => ({
//     marginRight: '0.75rem',
//     width: '1.25em',
//     textAlign: 'center',
//     color: isActiveParam ? StyleConstants.sidebarActiveText : StyleConstants.sidebarTextColor,
//     transition: 'color 0.2s ease', // Add transition to icon color
//   });

//   const collapsibleHeaderStyle = (to) => ({
//     ...navLinkBaseStyle,
//     marginTop: '0.2rem',
//     cursor: 'pointer',
//     backgroundColor: hoveredLink === `toggle-${to}` ? StyleConstants.sidebarHoverBg : 'transparent',
//   });

//   const submenuUlStyle = { listStyleType: 'none', paddingLeft: '0.5rem' };
//   const submenuLinkPaddingStyle = { paddingLeft: '1.5rem' };

//   const mobileToggleButtonStyle = { // This is for the button outside the sidebar for smallest screens
//     position: 'fixed', top: 'calc(var(--sidebar-top-margin, 20px) + 10px)', // Position relative to sidebar's top margin
//     left: '15px', zIndex: 1051,
//     backgroundColor: 'rgba(0,0,0,0.6)', // Darker, slightly transparent
//     color: 'white', border: 'none',
//     padding: '0.4rem 0.65rem', borderRadius: '0.25rem',
//     boxShadow: '0 2px 5px rgba(0,0,0,0.3)',
//   };

//   // New style for the persistent toggle button INSIDE the sidebar
//   const persistentToggleButtonStyle = (currentIsOpen) => ({
//     // Position it at the top right of the sidebar, or bottom
//     position: 'absolute',
//     top: '10px',
//     right: currentIsOpen ? '10px' : '50%', // Center when collapsed
//     transform: currentIsOpen ? 'none' : 'translateX(50%)', // Center when collapsed
//     zIndex: 10, // Above other sidebar content
//     background: 'none',//'rgba(255, 255, 255, 0.1)',
//     color: StyleConstants.sidebarTextColor,
//     border: 'none', //'1px solid rgba(255, 255, 255, 0.3)',
//     padding: '0.3rem 0.5rem',
//     borderRadius: '5px',
//     cursor: 'pointer',
//     transition: 'background-color 0.2s ease, right 0.35s ease-in-out, transform 0.35s ease-in-out', // Added right/transform transition
//   });
//   const persistentToggleButtonHoverStyle = {
//     backgroundColor: 'rgba(255, 255, 255, 0.2)',
//   };


//   // --- Helper Functions (renderNavLink, renderCollapsibleSection remain the same) ---
//   const renderNavLink = (to, icon, text, currentIsOpen, isSubmenu = false) => (
//     <li className="nav-item">
//       <NavLink
//         to={to}
//         onMouseEnter={() => setHoveredLink(to)}
//         onMouseLeave={() => setHoveredLink(null)}
//         className={({ isActive }) => isActive ? "active-sidebar-link" : ""}
//         style={({ isActive }) => getNavLinkStyle(to, isSubmenu, isActive)}
//       >
//         {({ isActive }) => ( 
//           <>
//             {typeof icon === 'string' ? 
//               <i className={`${icon} me-2`} style={iconStyleObject(isActive)}></i> : 
//               <FontAwesomeIcon icon={icon} style={iconStyleObject(isActive)} className="me-2 fa-fw" />}
//             {currentIsOpen ? text : ""}
//           </>
//         )}
//       </NavLink>
//     </li>
//   );

//   const renderCollapsibleSection = (label, iconComponent, isSectionOpen, toggleFunction, links, currentIsOpen) => (
//     <li className="nav-item">
//       <div
//         onClick={toggleFunction}
//         style={collapsibleHeaderStyle(label)}
//         onMouseEnter={() => setHoveredLink(`toggle-${label}`)}
//         onMouseLeave={() => setHoveredLink(null)}
//         role="button"
//         aria-expanded={isSectionOpen}
//       >
//         <span style={{ display: 'flex', alignItems: 'center', overflow: 'hidden' }}>
//           <span style={iconStyleObject(false)}>{iconComponent}</span>
//           {currentIsOpen ? label : ""}
//         </span>
//         {currentIsOpen && ( // Only show chevron if sidebar is open enough for it to make sense
//             <FontAwesomeIcon
//             icon={isSectionOpen ? faChevronUp : faChevronDown}
//             style={{ marginLeft: 'auto', color: StyleConstants.sidebarTextColor, transition: 'transform 0.2s ease-in-out' }}
//             />
//         )}
//       </div>
//       {currentIsOpen && isSectionOpen && (
//         <ul style={submenuUlStyle}>
//           {links.map(link => (
//             <li className="nav-item" key={link.to}>
//               <NavLink
//                 to={link.to}
//                 onMouseEnter={() => setHoveredLink(link.to)}
//                 onMouseLeave={() => setHoveredLink(null)}
//                 className={({ isActive }) => isActive ? "active-sidebar-link" : ""}
//                 style={({ isActive }) => ({
//                     ...getNavLinkStyle(link.to, true, isActive),
//                     ...submenuLinkPaddingStyle
//                 })}
//               >
//                 {({ isActive }) => ( 
//                   <>
//                     {typeof link.icon === 'string' ? 
//                       <i className={`${link.icon} me-2`} style={iconStyleObject(isActive)}></i> : 
//                       <FontAwesomeIcon icon={link.icon} style={iconStyleObject(isActive)} className="me-2 fa-fw"/>}
//                     {currentIsOpen ? link.text : ""}
//                   </>
//                 )}
//               </NavLink>
//             </li>
//           ))}
//         </ul>
//       )}
//     </li>
//   );

//   // --- Link data arrays (candidateLinks, hrLinks, interviewerLinks remain the same) ---
//     const candidateLinks = [
//     { to: "/jobapplication", icon: "fas fa-user", text: "Create Profile" },
//     { to: "/personalityquizform", icon: "fas fa-brain", text: "Personality Quiz" },
//     { to: "/Jobpostingresult", icon: "fas fa-briefcase", text: "Job Openings" },
//     { to: "/DocumentUpload", icon: "fas fa-file-alt", text: "Upload Document" },
//   ];
//   const hrLinks = [
//     { to: "/Jobposting", icon: "fas fa-plus-square", text: "Job Posting" },
//     { to: "/jobapplicationresult", icon: "fas fa-users", text: "Job Applicants" },
//     { to: "/interviewSchedule", icon: "fas fa-calendar-alt", text: "Schedule Interview" },
//     { to: "/InterviewerFeedback", icon: "fas fa-comments", text: "Interview Evaluation" },
//     { to: "/DocumentViewList", icon: "fas fa-folder-open", text: "Document View" },
//     { to: "/letterform", icon: faFileContract, text: "Offer Letter" },
//     { to: "/appoinmentletterform", icon: faFileContract, text: "Appointment Letter" },
//     { to: "/InterviewerDetailList", icon: "fas fa-id-badge", text: "Interviewers List" },
//     { to: "/resume-bank", icon: faDatabase, text: "Resume Bank" }
//   ];
//   const interviewerLinks = [
//     { to: "/InterviewerForm", icon: "fas fa-user-edit", text: "Interviewer Form" },
//     { to: "/evaluationForm", icon: "fas fa-clipboard-check", text: "Evaluation Form" },
//   ];

//   return (
//     <div style={sidebarContainerStyle}>
//       {/* Mobile toggle button - remains outside the sidebar nav */}
//       <button 
//         className="d-md-none" // Only show on small screens (Bootstrap class)
//         style={{
//             ...mobileToggleButtonStyle,
//             // Adjust left position based on sidebar visibility to avoid overlap when sidebar is forced open
//             left: isOpen && window.innerWidth < 768 ? 'calc(4.5rem + 15px)' : '15px', // Example adjustment
//             transition: 'left 0.3s ease-in-out',
//         }} 
//         onClick={toggleSidebar}
//        >
//         <FontAwesomeIcon icon={isOpen ? faTimes : faBars} />
//       </button>

//       <nav
//         className={`shadow h-100 ${isOpen ? "open" : ""}`}
//         style={sidebarStyle(isOpen)}
//       >
//         {/* Persistent Toggle Button - INSIDE the sidebar */}
//         <button
//           onClick={toggleSidebar}
//           style={{
//             ...persistentToggleButtonStyle(isOpen),
//             ...(hoveredLink === 'persistent-toggle' ? persistentToggleButtonHoverStyle : {})
//           }}
//           onMouseEnter={() => setHoveredLink('persistent-toggle')}
//           onMouseLeave={() => setHoveredLink(null)}
//           aria-label={isOpen ? "Collapse sidebar" : "Expand sidebar"}
//           className="d-none d-md-block" // Hide on small screens, show on medium and up
//         >
//           <FontAwesomeIcon icon={isOpen ? faAngleDoubleLeft : faAngleDoubleRight} />
//         </button>

//         <ul className="nav nav-pills flex-column mb-auto text-start" style={{ marginTop: isOpen? '40px': '20px' }}> {/* Adjust top margin for nav items to make space for toggle */}
//           {userRole === "Admin" && (
//             <>
//               {renderNavLink("/Admindashboard", "fas fa-home", "Home", isOpen)}
//               {renderNavLink("/crudHome", "fas fa-database", "Admin Panel", isOpen)}
//               {renderNavLink("/create-questions", FaQuestionCircle, "Create Questions", isOpen)}
//               {renderNavLink("/edit-dashboard", faEdit, "Edit Dashboard", isOpen)}
//               {renderCollapsibleSection("Candidate", <FaUserAlt />, isCandidate, toggleCandidate, candidateLinks, isOpen)}
//               {renderCollapsibleSection("HR", <FaUserAlt />, isHR, toggleHR, hrLinks, isOpen)}
//               {renderCollapsibleSection("Interviewer", <FaUserAlt />, isInterviewer, toggleInterviewer, interviewerLinks, isOpen)}
//               {renderNavLink("/feedback-list", "fas fa-list-alt", "Feedback List", isOpen)}
//               {renderNavLink("/pdf-viewer", "fas fa-file-pdf", "Scan Pdf", isOpen)}
//               {renderNavLink("/feedback", "fas fa-comment-dots", "Feedback", isOpen)}
//             </>
//           )}
//            {/* ... (Candidate, HR, Interviewer sections - no changes needed inside them for this feature) ... */}
//            {userRole === "Candidate" && (
//             <>
//               {renderNavLink("/dashboard", "fas fa-home", "Home", isOpen)}
//               {candidateLinks.map(link => renderNavLink(link.to, link.icon, link.text, isOpen))}
//               {renderNavLink("/pdf-viewer", "fas fa-file-pdf", "Scan Pdf", isOpen)}
//               {renderNavLink("/feedback", "fas fa-comment-dots", "Feedback", isOpen)}
//             </>
//           )}

//           {userRole === "HR" && (
//             <>
//               {renderNavLink("/HRDashboard", "fas fa-home", "Home", isOpen)}
//               {hrLinks.map(link => renderNavLink(link.to, link.icon, link.text, isOpen))}
//               {renderNavLink("/pdf-viewer", "fas fa-file-pdf", "Scan Pdf", isOpen)}
//               {renderNavLink("/feedback", "fas fa-comment-dots", "Feedback", isOpen)}
//               {renderNavLink("/feedback-list", "fas fa-list-alt", "Feedback List", isOpen)}
//             </>
//           )}

//           {userRole === "Interviewer" && (
//             <>
//               {renderNavLink("/InterviewerDashboard", "fas fa-home", "Home", isOpen)}
//               {interviewerLinks.map(link => renderNavLink(link.to, link.icon, link.text, isOpen))}
//               {renderNavLink("/feedback", "fas fa-comment-dots", "Feedback", isOpen)}
//             </>
//           )}
//         </ul>
//       </nav>
//       {/* JSX Style block for active links (if not using Next.js, remove or handle active state fully in JS) */}
//       <style jsx global>{`
//         .active-sidebar-link {
//           /* Primarily styled by getNavLinkStyle when active */
//         }
//         .active-sidebar-link i,
//         .active-sidebar-link svg {
//           /* Primarily styled by iconStyleObject when active */
//         }
//       `}</style>
//     </div>
//   );
// }

// import { NavLink } from "react-router-dom";
// import { useState, useEffect } from "react";
// import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
// import {
//   faEdit,
//   faBars,
//   faDatabase,
//   faTimes,
//   faChevronDown,
//   faChevronUp,
//   faFileContract,
//   faAngleDoubleLeft, // Icon for collapse
//   faAngleDoubleRight // Icon for expand
// } from "@fortawesome/free-solid-svg-icons";
// import { FaUserAlt, FaQuestionCircle } from "react-icons/fa";

// const StyleConstants = {
//   sidebarGradient: 'linear-gradient(145deg, #ff9800, #f46f30, #e9491c)',
//   sidebarTextColor: 'white',
//   sidebarActiveBg: 'white',
//   sidebarActiveText: '#e9491c',
//   sidebarHoverBg: 'rgba(255, 255, 255, 0.1)',
//   sidebarBorderRadius: '15px',
//   // sidebarTopMargin: '20px', // We'll use padding on container now
//   surroundingPadding: '10px', // New constant for the padding around the sidebar
// };


// export default function Sidebar() {
//   const userRole = localStorage.getItem("User_Role");
//   const [isOpen, setIsOpen] = useState(window.innerWidth >= 768);
//   const [isCandidate, setIsCandidate] = useState(false);
//   const [isHR, setIsHR] = useState(false);
//   const [isInterviewer, setIsInterviewer] = useState(false);
//   const [hoveredLink, setHoveredLink] = useState(null);

//   const toggleSidebar = () => setIsOpen(!isOpen);
//   const toggleCandidate = () => setIsCandidate(!isCandidate);
//   const toggleHR = () => setIsHR(!isHR);
//   const toggleInterviewer = () => setIsInterviewer(!isInterviewer);

//   useEffect(() => {
//     const handleResize = () => {
//         if (window.innerWidth < 768) {
//             setIsOpen(false);
//         }
//     };
//     window.addEventListener("resize", handleResize);
//     return () => window.removeEventListener("resize", handleResize);
//   }, []);

//   const sidebarContainerStyle = {
//     height: '100vh', // Container takes full viewport height
//     display: 'flex',
//     position: 'relative',
//     padding: StyleConstants.surroundingPadding, // ADDED: Padding around the sidebar
//     boxSizing: 'border-box', // Ensures padding is included in height/width calculation
//   };

//   const sidebarStyle = (currentIsOpen) => ({
//     backgroundImage: StyleConstants.sidebarGradient,
//     color: StyleConstants.sidebarTextColor,
//     transition: 'width 0.3s ease-in-out, min-width 0.3s ease-in-out',
//     // Apply border radius to all corners since it's now fully contained
//     borderRadius: StyleConstants.sidebarBorderRadius,
//     paddingTop: '0.5rem',
//     paddingBottom: '0.5rem',
//     display: 'flex',
//     flexDirection: 'column',
//     overflowX: 'hidden',
//     width: currentIsOpen ? "16rem" : "4.5rem",
//     minWidth: currentIsOpen ? "16rem" : "4.5rem",
//     boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
//     position: 'relative',
//     height: '100%', // MODIFIED: Sidebar takes full height of its padded container
//     // marginTop: StyleConstants.sidebarTopMargin, // REMOVED: Container's padding handles this
//   });

//   const navLinkBaseStyle = {
//     color: StyleConstants.sidebarTextColor,
//     borderRadius: '8px',
//     margin: '0.2rem 0.5rem',
//     padding: '0.6rem 0.8rem',
//     display: 'flex',
//     alignItems: 'center',
//     whiteSpace: 'nowrap',
//     overflow: 'hidden',
//     textOverflow: 'ellipsis',
//     textDecoration: 'none',
//     transition: 'background-color 0.2s ease, color 0.2s ease',
//   };

//   const getNavLinkStyle = (to, isSubmenu = false, isActive) => {
//     let linkStyle = {
//       ...navLinkBaseStyle,
//       marginTop: isSubmenu ? '0.1rem' : '0.2rem',
//       marginBottom: isSubmenu ? '0.1rem' : '0.2rem',
//       fontSize: isSubmenu ? '0.9rem' : '1rem',
//       color: isSubmenu ? 'rgba(255, 255, 255, 0.85)' : StyleConstants.sidebarTextColor,
//     };
//     if (isActive) {
//       linkStyle = {
//         ...linkStyle,
//         backgroundColor: StyleConstants.sidebarActiveBg,
//         color: StyleConstants.sidebarActiveText,
//         fontWeight: isSubmenu ? 500 : 600,
//       };
//     } else if (hoveredLink === to) {
//       linkStyle = { ...linkStyle, backgroundColor: StyleConstants.sidebarHoverBg };
//     }
//     return linkStyle;
//   };
  
//   const iconStyleObject = (isActiveParam) => ({
//     marginRight: '0.75rem',
//     width: '1.25em',
//     textAlign: 'center',
//     color: isActiveParam ? StyleConstants.sidebarActiveText : StyleConstants.sidebarTextColor,
//     transition: 'color 0.2s ease',
//   });

//   const collapsibleHeaderStyle = (to) => ({
//     ...navLinkBaseStyle,
//     marginTop: '0.2rem',
//     cursor: 'pointer',
//     backgroundColor: hoveredLink === `toggle-${to}` ? StyleConstants.sidebarHoverBg : 'transparent',
//   });

//   const submenuUlStyle = { listStyleType: 'none', paddingLeft: '0.5rem' };
//   const submenuLinkPaddingStyle = { paddingLeft: '1.5rem' };

//   const mobileToggleButtonStyle = {
//     position: 'fixed',
//     // Adjust 'top' and 'left' to account for the container's padding
//     top: `calc(${StyleConstants.surroundingPadding} + 10px)`, 
//     left: `calc(${StyleConstants.surroundingPadding} + 15px)`,
//     zIndex: 1051,
//     backgroundColor: 'rgba(0,0,0,0.6)',
//     color: 'white', border: 'none',
//     padding: '0.4rem 0.65rem', borderRadius: '0.25rem',
//     boxShadow: '0 2px 5px rgba(0,0,0,0.3)',
//   };

//   const persistentToggleButtonStyle = (currentIsOpen) => ({
//     position: 'absolute',
//     top: '10px',
//     right: currentIsOpen ? '10px' : '50%',
//     transform: currentIsOpen ? 'none' : 'translateX(50%)',
//     zIndex: 10,
//     background: 'none',
//     color: StyleConstants.sidebarTextColor,
//     border: 'none',
//     padding: '0.3rem 0.5rem',
//     borderRadius: '5px',
//     cursor: 'pointer',
//     transition: 'background-color 0.2s ease, right 0.35s ease-in-out, transform 0.35s ease-in-out',
//   });
//   const persistentToggleButtonHoverStyle = {
//     backgroundColor: 'rgba(255, 255, 255, 0.2)',
//   };

//   const renderNavLink = (to, icon, text, currentIsOpen, isSubmenu = false) => (
//     <li className="nav-item">
//       <NavLink
//         to={to}
//         onMouseEnter={() => setHoveredLink(to)}
//         onMouseLeave={() => setHoveredLink(null)}
//         className={({ isActive }) => isActive ? "active-sidebar-link" : ""}
//         style={({ isActive }) => getNavLinkStyle(to, isSubmenu, isActive)}
//       >
//         {({ isActive }) => ( 
//           <>
//             {typeof icon === 'string' ? 
//               <i className={`${icon} me-2`} style={iconStyleObject(isActive)}></i> : 
//               <FontAwesomeIcon icon={icon} style={iconStyleObject(isActive)} className="me-2 fa-fw" />}
//             {currentIsOpen ? text : ""}
//           </>
//         )}
//       </NavLink>
//     </li>
//   );

//   const renderCollapsibleSection = (label, iconComponent, isSectionOpen, toggleFunction, links, currentIsOpen) => (
//     <li className="nav-item">
//       <div
//         onClick={toggleFunction}
//         style={collapsibleHeaderStyle(label)}
//         onMouseEnter={() => setHoveredLink(`toggle-${label}`)}
//         onMouseLeave={() => setHoveredLink(null)}
//         role="button"
//         aria-expanded={isSectionOpen}
//       >
//         <span style={{ display: 'flex', alignItems: 'center', overflow: 'hidden' }}>
//           <span style={iconStyleObject(false)}>{iconComponent}</span>
//           {currentIsOpen ? label : ""}
//         </span>
//         {currentIsOpen && (
//             <FontAwesomeIcon
//             icon={isSectionOpen ? faChevronUp : faChevronDown}
//             style={{ marginLeft: 'auto', color: StyleConstants.sidebarTextColor, transition: 'transform 0.2s ease-in-out' }}
//             />
//         )}
//       </div>
//       {currentIsOpen && isSectionOpen && (
//         <ul style={submenuUlStyle}>
//           {links.map(link => (
//             <li className="nav-item" key={link.to}>
//               <NavLink
//                 to={link.to}
//                 onMouseEnter={() => setHoveredLink(link.to)}
//                 onMouseLeave={() => setHoveredLink(null)}
//                 className={({ isActive }) => isActive ? "active-sidebar-link" : ""}
//                 style={({ isActive }) => ({
//                     ...getNavLinkStyle(link.to, true, isActive),
//                     ...submenuLinkPaddingStyle
//                 })}
//               >
//                 {({ isActive }) => ( 
//                   <>
//                     {typeof link.icon === 'string' ? 
//                       <i className={`${link.icon} me-2`} style={iconStyleObject(isActive)}></i> : 
//                       <FontAwesomeIcon icon={link.icon} style={iconStyleObject(isActive)} className="me-2 fa-fw"/>}
//                     {currentIsOpen ? link.text : ""}
//                   </>
//                 )}
//               </NavLink>
//             </li>
//           ))}
//         </ul>
//       )}
//     </li>
//   );

//   const candidateLinks = [
//     { to: "/jobapplication", icon: "fas fa-user", text: "Create Profile" },
//     // { to: "/personalityquizform", icon: "fas fa-brain", text: "Personality Quiz" },
//     { to: "/Jobpostingresult", icon: "fas fa-briefcase", text: "Job Openings" },
//     { to: "/DocumentUpload", icon: "fas fa-file-alt", text: "Upload Document" },
//     { to: "/contact-us", icon: "fas fa-headset", text: "Contact Us" }

     
//   ];
//   const hrLinks = [
//     { to: "/Jobposting", icon: "fas fa-plus-square", text: "Job Posting" },
//     { to: "/jobapplicationresult", icon: "fas fa-users", text: "Job Applicants" },
//     { to: "/interviewSchedule", icon: "fas fa-calendar-alt", text: "Schedule Interview" },
//     { to: "/InterviewerFeedback", icon: "fas fa-comments", text: "Interview Evaluation" },
//     { to: "/DocumentViewList", icon: "fas fa-folder-open", text: "Document View" },
//     { to: "/letterform", icon: faFileContract, text: "Offer Letter" },
//     { to: "/appoinmentletterform", icon: faFileContract, text: "Appointment Letter" },
//     { to: "/InterviewerDetailList", icon: "fas fa-id-badge", text: "Interviewers List" },
//     { to: "/resume-bank", icon: faDatabase, text: "Resume Bank" }
//   ];
//   const interviewerLinks = [
//     { to: "/InterviewerForm", icon: "fas fa-user-edit", text: "Interviewer Form" },
//     { to: "/evaluationForm", icon: "fas fa-clipboard-check", text: "Evaluation Form" },
//   ];

//   return (
//     // The div with sidebarContainerStyle is now the main element for the sidebar area
//     <div style={sidebarContainerStyle}>
//       {/* Mobile toggle button's position is now relative to the padded container */}
//       <button 
//         className="d-md-none"
//         style={{
//             ...mobileToggleButtonStyle,
//             left: isOpen && window.innerWidth < 768 
//                 ? `calc(4.5rem + ${StyleConstants.surroundingPadding} + 15px)` // Adjust if sidebar is open on mobile
//                 : `calc(${StyleConstants.surroundingPadding} + 15px)`, 
//             transition: 'left 0.3s ease-in-out',
//         }} 
//         onClick={toggleSidebar}
//        >
//         <FontAwesomeIcon icon={isOpen ? faTimes : faBars} />
//       </button>

//       {/* The nav element is the actual sidebar visuals */}
//       <nav
//         className={`shadow ${isOpen ? "open" : ""}`} // Removed h-100 here, will be 100% of parent
//         style={sidebarStyle(isOpen)}
//       >
//         <button
//           onClick={toggleSidebar}
//           style={{
//             ...persistentToggleButtonStyle(isOpen),
//             ...(hoveredLink === 'persistent-toggle' ? persistentToggleButtonHoverStyle : {})
//           }}
//           onMouseEnter={() => setHoveredLink('persistent-toggle')}
//           onMouseLeave={() => setHoveredLink(null)}
//           aria-label={isOpen ? "Collapse sidebar" : "Expand sidebar"}
//           className="d-none d-md-block"
//         >
//           <FontAwesomeIcon icon={isOpen ? faAngleDoubleLeft : faAngleDoubleRight} />
//         </button>

//                 <ul className="nav nav-pills flex-column mb-auto text-start" style={{ marginTop: isOpen ? '40px' : '45px' }}>
//           {userRole === "Admin" && (
//             <>
//               {renderNavLink("/Admindashboard", "fas fa-home", "Home", isOpen)}
//               {renderNavLink("/crudHome", "fas fa-database", "Admin Panel", isOpen)}
//               {renderNavLink("/create-questions", faEdit, "Create Questions", isOpen)}
//               {renderNavLink("/edit-dashboard", faEdit, "Edit Dashboard", isOpen)}
//               {renderCollapsibleSection("Candidate", <FaUserAlt />, isCandidate, toggleCandidate, candidateLinks, isOpen)}
//               {renderCollapsibleSection("HR", <FaUserAlt />, isHR, toggleHR, hrLinks, isOpen)}
//               {renderCollapsibleSection("Interviewer", <FaUserAlt />, isInterviewer, toggleInterviewer, interviewerLinks, isOpen)}
//               {renderNavLink("/feedback-list", "fas fa-list-alt", "Feedback List", isOpen)}
//               {/* {renderNavLink("/pdf-viewer", "fas fa-file-pdf", "Scan Pdf", isOpen)} */}
//               {/* {renderNavLink("/feedback", "fas fa-comment-dots", "Feedback", isOpen)} */}
//             </>
//           )}
//            {userRole === "Candidate" && (
//             <>
//               {renderNavLink("/dashboard", "fas fa-home", "Home", isOpen)}
//               {candidateLinks.map(link => renderNavLink(link.to, link.icon, link.text, isOpen))}
//               {/* {renderNavLink("/pdf-viewer", "fas fa-file-pdf", "Scan Pdf", isOpen)} */}
//               {/* {renderNavLink("/feedback", "fas fa-comment-dots", "Feedback", isOpen)} */}
//             </>
//           )}
//           {userRole === "HR" && (
//             <>
//               {renderNavLink("/HRDashboard", "fas fa-home", "Home", isOpen)}
//               {hrLinks.map(link => renderNavLink(link.to, link.icon, link.text, isOpen))}
//               {/* {renderNavLink("/pdf-viewer", "fas fa-file-pdf", "Scan Pdf", isOpen)} */}
//               {/* {renderNavLink("/feedback", "fas fa-comment-dots", "Feedback", isOpen)} */}
//               {/* {renderNavLink("/feedback-list", "fas fa-list-alt", "Feedback List", isOpen)} */}
//             </>
//           )}
//           {userRole === "Interviewer" && (
//             <>
//               {renderNavLink("/InterviewerDashboard", "fas fa-home", "Home", isOpen)}
//               {interviewerLinks.map(link => renderNavLink(link.to, link.icon, link.text, isOpen))}
//               {/* {renderNavLink("/feedback", "fas fa-comment-dots", "Feedback", isOpen)} */}
//             </>
//           )}
//         </ul>
//       </nav>
//       <style jsx global>{`
//         .active-sidebar-link {}
//         .active-sidebar-link i,
//         .active-sidebar-link svg {}
//       `}</style>
//     </div>
//   );
// }